<script>
import {
  createShippingFeePolicy,
  getShippingFeePolicies,
  updateShippingFeePolicy,
} from '@/api/product.js'
import { getBranchList } from '@/api/branch.js'
import AppPagination from '@/components/common/AppPagination.vue'

const today = () => new Date().toISOString().substring(0, 10)

const emptyForm = () => ({
  policyNm: '',
  scopeType: 'HEAD',
  branchCode: '',
  calculationBasis: 'SHIPMENT',
  defaultYn: 'N',
  activeYn: 'Y',
  startDt: today(),
  endDt: '',
  description: '',
  baseMaxWeightKg: 10,
  baseFeeAmount: 25000,
  overUnitWeightKg: 1,
  overUnitFeeAmount: 0,
  weightRules: [
    { minWeightKg: 0, maxWeightKg: 10, feeAmount: 25000, activeYn: 'Y' },
    { minWeightKg: 10, maxWeightKg: null, feeAmount: 0, activeYn: 'Y' },
  ],
  areaSurchargeRows: [
    { minWeightKg: 0, maxWeightKg: 10, jejuSurchargeAmount: 0, remoteSurchargeAmount: 0 },
    { minWeightKg: 10, maxWeightKg: null, jejuSurchargeAmount: 0, remoteSurchargeAmount: 0 },
  ],
  jejuSurchargeAmount: 0,
  remoteSurchargeAmount: 0,
})

export default {
  name: 'ShippingFeePolicyPage',
  components: { AppPagination },
  data() {
    return {
      search: {
        keyword: '',
        scopeType: '',
        activeYn: '',
      },
      scopeOptions: [
        { title: '전체', value: '' },
        { title: '본사 공통', value: 'HEAD' },
        { title: '지점별', value: 'BRANCH' },
      ],
      activeOptions: [
        { title: '전체', value: '' },
        { title: '사용', value: 'Y' },
        { title: '미사용', value: 'N' },
      ],
      basisOptions: [
        { title: '배송 묶음 기준', value: 'SHIPMENT' },
        { title: '주문 전체 기준', value: 'ORDER' },
      ],
      branchOptions: [],
      items: [],
      total: 0,
      page: 1,
      size: 10,
      loading: false,
      dialog: false,
      saving: false,
      editTarget: null,
      form: emptyForm(),
    }
  },
  computed: {
    formTitle() {
      return this.editTarget ? '배송비 정책 수정' : '배송비 정책 등록'
    },
  },
  mounted() {
    this.fetchBranches()
    this.fetchList()
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await getBranchList({ page: 1, size: 500 })
        const rows = res.data?.data?.items || []
        this.branchOptions = rows.map(item => ({
          title: item.branchName || item.branchNm || item.branchCode,
          value: item.branchCode,
        }))
      } catch (error) {
        console.error('[ShippingFeePolicyPage] fetchBranches error:', error)
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getShippingFeePolicies({
          keyword: this.search.keyword || undefined,
          scopeType: this.search.scopeType || undefined,
          activeYn: this.search.activeYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('[ShippingFeePolicyPage] fetchList error:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', scopeType: '', activeYn: '' }
      this.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
    openCreateDialog() {
      this.editTarget = null
      this.form = emptyForm()
      this.dialog = true
    },
    openEditDialog(item) {
      this.editTarget = item
      const baseRule = item.weightRules?.find(rule => rule.ruleType === 'BASE') || {}
      const overRule = item.weightRules?.find(rule => rule.ruleType === 'OVER_WEIGHT') || {}
      const jeju = item.areaSurcharges?.find(area => area.regionType === 'JEJU') || {}
      const remote = item.areaSurcharges?.find(area => area.regionType === 'REMOTE') || {}
      const weightRules = this.toEditableWeightRules(item)
      const areaSurchargeRows = this.toEditableAreaSurchargeRows(item, weightRules)
      this.form = {
        policyNm: item.policyNm || '',
        scopeType: item.scopeType || 'HEAD',
        branchCode: item.branchCode || '',
        calculationBasis: item.calculationBasis || 'SHIPMENT',
        defaultYn: item.defaultYn || 'N',
        activeYn: item.activeYn || 'Y',
        startDt: this.formatDate(item.startDt) || today(),
        endDt: this.formatDate(item.endDt) || '',
        description: item.description || '',
        baseMaxWeightKg: Number(baseRule.maxWeightKg ?? 10),
        baseFeeAmount: Number(baseRule.feeAmount ?? 25000),
        overUnitWeightKg: Number(overRule.unitWeightKg ?? 1),
        overUnitFeeAmount: Number(overRule.unitFeeAmount ?? 0),
        weightRules,
        areaSurchargeRows,
        jejuSurchargeAmount: Number(jeju.surchargeAmount ?? 0),
        remoteSurchargeAmount: Number(remote.surchargeAmount ?? 0),
      }
      this.dialog = true
    },
    toEditableWeightRules(item) {
      const rangeRules = (item.weightRules || [])
        .filter(rule => rule.ruleType === 'RANGE')
        .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
        .map(rule => ({
          minWeightKg: Number(rule.minWeightKg ?? 0),
          maxWeightKg: rule.maxWeightKg === null || rule.maxWeightKg === undefined ? null : Number(rule.maxWeightKg),
          feeAmount: Number(rule.feeAmount ?? 0),
          activeYn: rule.activeYn || 'Y',
        }))
      if (rangeRules.length > 0) return rangeRules

      const baseRule = item.weightRules?.find(rule => rule.ruleType === 'BASE') || {}
      const overRule = item.weightRules?.find(rule => rule.ruleType === 'OVER_WEIGHT') || {}
      const baseMax = Number(baseRule.maxWeightKg ?? 10)
      return [
        {
          minWeightKg: 0,
          maxWeightKg: baseMax,
          feeAmount: Number(baseRule.feeAmount ?? 25000),
          activeYn: 'Y',
        },
        {
          minWeightKg: baseMax,
          maxWeightKg: null,
          feeAmount: Number(overRule.feeAmount ?? overRule.unitFeeAmount ?? 0),
          activeYn: 'Y',
        },
      ]
    },
    toEditableAreaSurchargeRows(item, weightRules) {
      const surcharges = item.areaSurcharges || []
      const firstJeju = surcharges.find(area => area.regionType === 'JEJU') || {}
      const firstRemote = surcharges.find(area => area.regionType === 'REMOTE') || {}
      return weightRules.map(rule => ({
        minWeightKg: rule.minWeightKg,
        maxWeightKg: rule.maxWeightKg,
        jejuSurchargeAmount: this.findAreaSurchargeAmount(surcharges, 'JEJU', rule, firstJeju.surchargeAmount),
        remoteSurchargeAmount: this.findAreaSurchargeAmount(surcharges, 'REMOTE', rule, firstRemote.surchargeAmount),
      }))
    },
    findAreaSurchargeAmount(surcharges, regionType, weightRule, fallback = 0) {
      const matched = surcharges.find(area =>
        area.regionType === regionType
        && Number(area.minWeightKg ?? 0) === Number(weightRule.minWeightKg ?? 0)
        && (
          (area.maxWeightKg === null || area.maxWeightKg === undefined)
            ? weightRule.maxWeightKg === null || weightRule.maxWeightKg === undefined
            : Number(area.maxWeightKg) === Number(weightRule.maxWeightKg)
        ))
      return Number(matched?.surchargeAmount ?? fallback ?? 0)
    },
    syncAreaSurchargeRows() {
      const previousRows = this.form.areaSurchargeRows || []
      this.form.areaSurchargeRows = this.form.weightRules.map(rule => {
        const previous = previousRows.find(row =>
          Number(row.minWeightKg ?? 0) === Number(rule.minWeightKg ?? 0)
          && (
            (row.maxWeightKg === null || row.maxWeightKg === undefined || row.maxWeightKg === '')
              ? rule.maxWeightKg === null || rule.maxWeightKg === undefined || rule.maxWeightKg === ''
              : Number(row.maxWeightKg) === Number(rule.maxWeightKg)
          ))
        return {
          minWeightKg: rule.minWeightKg,
          maxWeightKg: rule.maxWeightKg,
          jejuSurchargeAmount: Number(previous?.jejuSurchargeAmount ?? 0),
          remoteSurchargeAmount: Number(previous?.remoteSurchargeAmount ?? 0),
        }
      })
    },
    addWeightRule() {
      const last = this.form.weightRules[this.form.weightRules.length - 1]
      let start = last?.maxWeightKg ?? last?.minWeightKg ?? 0
      if (last && (last.maxWeightKg === null || last.maxWeightKg === '')) {
        last.maxWeightKg = Number(last.minWeightKg || 0) + 10
        start = last.maxWeightKg
      }
      this.form.weightRules.push({
        minWeightKg: start,
        maxWeightKg: null,
        feeAmount: 0,
        activeYn: 'Y',
      })
      this.syncAreaSurchargeRows()
    },
    removeWeightRule(index) {
      if (this.form.weightRules.length <= 1) {
        alert('중량 구간은 최소 1개 이상 필요합니다.')
        return
      }
      this.form.weightRules.splice(index, 1)
      this.syncAreaSurchargeRows()
    },
    validateForm() {
      if (!this.form.policyNm) return '정책명을 입력해 주세요.'
      if (this.form.scopeType === 'BRANCH' && !this.form.branchCode) return '지점별 정책은 지점을 선택해야 합니다.'
      if (!this.form.startDt) return '적용 시작일을 입력해 주세요.'
      if (!this.form.weightRules.length) return '중량 기준 배송비를 1개 이상 입력해 주세요.'
      let expectedMin = 0
      for (let i = 0; i < this.form.weightRules.length; i += 1) {
        const rule = this.form.weightRules[i]
        const min = Number(rule.minWeightKg)
        const max = rule.maxWeightKg === null || rule.maxWeightKg === '' ? null : Number(rule.maxWeightKg)
        const fee = Number(rule.feeAmount)
        if (Number.isNaN(min) || min < 0 || Number.isNaN(fee) || fee < 0 || (max !== null && (Number.isNaN(max) || max <= min))) {
          return '중량 구간과 배송비를 확인해 주세요.'
        }
        if (min !== expectedMin) return '중량 구간은 빈 구간 없이 이어져야 합니다.'
        if (max === null && i < this.form.weightRules.length - 1) return '무제한 중량 구간은 마지막에만 입력할 수 있습니다.'
        expectedMin = max
      }
      for (const row of this.form.areaSurchargeRows || []) {
        const jeju = Number(row.jejuSurchargeAmount)
        const remote = Number(row.remoteSurchargeAmount)
        if (Number.isNaN(jeju) || jeju < 0 || Number.isNaN(remote) || remote < 0) {
          return '지역 추가 배송비는 0 이상으로 입력해야 합니다.'
        }
      }
      return ''
    },
    async savePolicy() {
      const message = this.validateForm()
      if (message) {
        alert(message)
        return
      }
      this.saving = true
      const payload = {
        ...this.form,
        branchCode: this.form.scopeType === 'BRANCH' ? this.form.branchCode : null,
        endDt: this.form.endDt || null,
        baseMaxWeightKg: Number(this.form.baseMaxWeightKg),
        baseFeeAmount: Number(this.form.baseFeeAmount),
        overUnitWeightKg: Number(this.form.overUnitWeightKg),
        overUnitFeeAmount: Number(this.form.overUnitFeeAmount),
        weightRules: this.form.weightRules.map(rule => ({
          minWeightKg: Number(rule.minWeightKg),
          maxWeightKg: rule.maxWeightKg === null || rule.maxWeightKg === '' ? null : Number(rule.maxWeightKg),
          feeAmount: Number(rule.feeAmount),
          activeYn: rule.activeYn || 'Y',
        })),
        areaSurchargeRules: (this.form.areaSurchargeRows || []).flatMap((row, index) => {
          const weightRule = this.form.weightRules[index] || row
          const minWeightKg = Number(weightRule.minWeightKg)
          const maxWeightKg = weightRule.maxWeightKg === null || weightRule.maxWeightKg === '' ? null : Number(weightRule.maxWeightKg)
          return [
            {
              regionType: 'JEJU',
              minWeightKg,
              maxWeightKg,
              surchargeAmount: Number(row.jejuSurchargeAmount || 0),
              activeYn: 'Y',
            },
            {
              regionType: 'REMOTE',
              minWeightKg,
              maxWeightKg,
              surchargeAmount: Number(row.remoteSurchargeAmount || 0),
              activeYn: 'Y',
            },
          ]
        }),
        jejuSurchargeAmount: Number(this.form.jejuSurchargeAmount || 0),
        remoteSurchargeAmount: Number(this.form.remoteSurchargeAmount || 0),
      }
      try {
        const res = this.editTarget
          ? await updateShippingFeePolicy(this.editTarget.shippingFeePolicyCode, payload)
          : await createShippingFeePolicy(payload)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        } else {
          alert(res.data?.message || '배송비 정책 저장에 실패했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '배송비 정책 저장 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },
    scopeLabel(value) {
      return value === 'BRANCH' ? '지점별' : '본사 공통'
    },
    activeLabel(value) {
      return value === 'Y' ? '사용' : '미사용'
    },
    basisLabel(value) {
      return value === 'ORDER' ? '주문 전체' : '배송 묶음'
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : ''
    },
    formatAmount(value) {
      return `${Number(value || 0).toLocaleString('ko-KR')}원`
    },
    ruleSummary(item) {
      const rules = this.toEditableWeightRules(item)
      if (rules.length === 0) return '-'
      const preview = rules.slice(0, 2).map((rule, index) => this.weightRuleLabel(rule, index)).join(' / ')
      return rules.length > 2 ? `${preview} 외 ${rules.length - 2}개` : preview
    },
    weightRuleRangeLabel(rule, index) {
      const min = Number(rule.minWeightKg || 0).toLocaleString('ko-KR')
      const max = rule.maxWeightKg === null || rule.maxWeightKg === undefined || rule.maxWeightKg === ''
        ? null
        : Number(rule.maxWeightKg).toLocaleString('ko-KR')
      return max === null
        ? `${min}kg 초과`
        : index === 0
          ? `${max}kg 이하`
          : `${min}kg 초과 ~ ${max}kg 이하`
    },
    weightRuleLabel(rule, index) {
      const min = Number(rule.minWeightKg || 0).toLocaleString('ko-KR')
      const max = rule.maxWeightKg === null || rule.maxWeightKg === undefined || rule.maxWeightKg === ''
        ? null
        : Number(rule.maxWeightKg).toLocaleString('ko-KR')
      const range = max === null
        ? `${min}kg 초과`
        : index === 0
          ? `${max}kg 이하`
          : `${min}kg 초과 ~ ${max}kg 이하`
      return `${range} ${this.formatAmount(rule.feeAmount)}`
    },
    areaSummary(item) {
      const jeju = item.areaSurcharges?.find(area => area.regionType === 'JEJU') || {}
      const remote = item.areaSurcharges?.find(area => area.regionType === 'REMOTE') || {}
      return `제주 ${this.formatAmount(jeju.surchargeAmount)} / 도서산간 ${this.formatAmount(remote.surchargeAmount)}`
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">배송비 관리</div>
        <div class="text-body-2 text-grey mt-1">중량 기준 배송비와 제주/도서산간 추가 배송비를 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">배송비 정책 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <div class="shipping-filter">
          <v-text-field
            v-model="search.keyword"
            label="검색"
            placeholder="정책명, 지점명"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @keyup.enter="handleSearch"
          />
          <v-select v-model="search.scopeType" :items="scopeOptions" label="정책 범위" variant="outlined" density="compact" hide-details />
          <v-select v-model="search.activeYn" :items="activeOptions" label="사용 여부" variant="outlined" density="compact" hide-details />
          <div class="shipping-filter__actions">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          배송비 정책 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th>정책명</th>
            <th>범위</th>
            <th>계산 기준</th>
            <th>중량 기준</th>
            <th>지역 추가비</th>
            <th>기본</th>
            <th>사용</th>
            <th>적용기간</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.shippingFeePolicyCode" class="policy-row" @click="openEditDialog(item)">
            <td>
              <div class="font-weight-bold">{{ item.policyNm }}</div>
            </td>
            <td>
              <v-chip size="small" :color="item.scopeType === 'HEAD' ? 'primary' : 'info'" label>{{ scopeLabel(item.scopeType) }}</v-chip>
              <div v-if="item.branchName" class="text-caption mt-1">{{ item.branchName }}</div>
            </td>
            <td>{{ basisLabel(item.calculationBasis) }}</td>
            <td>{{ ruleSummary(item) }}</td>
            <td>{{ areaSummary(item) }}</td>
            <td>
              <v-chip size="small" :color="item.defaultYn === 'Y' ? 'warning' : 'grey'" label>{{ item.defaultYn }}</v-chip>
            </td>
            <td>
              <v-chip size="small" :color="item.activeYn === 'Y' ? 'success' : 'grey'" label>{{ activeLabel(item.activeYn) }}</v-chip>
            </td>
            <td>{{ formatDate(item.startDt) }} ~ {{ formatDate(item.endDt) || '상시' }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="8" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">mdi-truck-remove-outline</v-icon>
              등록된 배송비 정책이 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4">
        <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="1040" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">{{ formTitle }}</v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.policyNm" label="정책명 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="form.scopeType"
                :items="scopeOptions.filter(item => item.value)"
                label="정책 범위 *"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="form.calculationBasis" :items="basisOptions" label="계산 기준 *" variant="outlined" />
            </v-col>
            <v-col v-if="form.scopeType === 'BRANCH'" cols="12" md="6">
              <v-select
                v-model="form.branchCode"
                :items="branchOptions"
                item-title="title"
                item-value="value"
                label="적용 지점 *"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="form.defaultYn" :items="[{ title: 'Y', value: 'Y' }, { title: 'N', value: 'N' }]" label="기본 정책" variant="outlined" />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="form.activeYn" :items="[{ title: '사용', value: 'Y' }, { title: '미사용', value: 'N' }]" label="사용 여부" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.startDt" type="date" label="적용 시작일 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.endDt" type="date" label="적용 종료일" variant="outlined" />
            </v-col>
          </v-row>

          <div class="shipping-section">
            <div class="shipping-section__head">
              <div>
                <div class="shipping-section__title">중량 구간별 배송비</div>
                <div class="text-caption text-medium-emphasis">
                  첫 구간은 0kg부터 시작하며, 두 번째 구간부터는 시작 중량 초과 기준으로 계산됩니다.
                </div>
              </div>
              <v-btn size="small" variant="outlined" prepend-icon="mdi-plus" @click="addWeightRule">구간 추가</v-btn>
            </div>
            <v-table density="compact" class="weight-rule-table">
              <thead>
                <tr>
                  <th>구간</th>
                  <th>시작 중량(kg)</th>
                  <th>종료 중량(kg)</th>
                  <th>배송비</th>
                  <th class="text-center">삭제</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(rule, index) in form.weightRules" :key="index">
                  <td class="text-caption text-medium-emphasis">
                    {{ weightRuleRangeLabel(rule, index) }}
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="rule.minWeightKg"
                      type="number"
                      min="0"
                      step="0.001"
                      density="compact"
                      variant="outlined"
                      hide-details
                      suffix="kg"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="rule.maxWeightKg"
                      type="number"
                      min="0"
                      step="0.001"
                      density="compact"
                      variant="outlined"
                      hide-details
                      suffix="kg"
                      placeholder="무제한"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="rule.feeAmount"
                      type="number"
                      min="0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      suffix="원"
                    />
                  </td>
                  <td class="text-center">
                    <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click.stop="removeWeightRule(index)" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <div class="shipping-section">
            <div class="shipping-section__title">지역별 중량 추가 배송비</div>
            <v-table density="compact" class="weight-rule-table area-surcharge-table mt-3">
              <thead>
                <tr>
                  <th>구간</th>
                  <th>제주 추가비</th>
                  <th>도서산간 추가비</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in form.areaSurchargeRows" :key="`area-${index}`">
                  <td class="text-caption text-medium-emphasis">
                    {{ weightRuleRangeLabel(form.weightRules[index] || row, index) }}
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="row.jejuSurchargeAmount"
                      type="number"
                      min="0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      suffix="원"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="row.remoteSurchargeAmount"
                      type="number"
                      min="0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      suffix="원"
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
          <v-textarea v-model="form.description" label="설명" variant="outlined" rows="3" />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="savePolicy">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.shipping-filter {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 220px 180px auto;
  gap: 12px;
  align-items: center;
}

.shipping-filter__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.policy-row {
  cursor: pointer;
}

.policy-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}

.shipping-section {
  border: 1px solid rgba(205, 178, 122, 0.24);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}

.shipping-section__title {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.shipping-section__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.weight-rule-table {
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.72);
  color: rgba(226, 232, 240, 0.96);
}

.weight-rule-table :deep(table) {
  background: transparent;
  table-layout: fixed;
  width: 100%;
}

.weight-rule-table :deep(thead) {
  background: rgba(31, 41, 55, 0.92);
}

.weight-rule-table :deep(th) {
  white-space: nowrap;
  color: rgba(226, 232, 240, 0.96);
  border-bottom-color: rgba(148, 163, 184, 0.24) !important;
  font-weight: 700;
}

.weight-rule-table :deep(th:nth-child(1)),
.weight-rule-table :deep(td:nth-child(1)) {
  width: 190px;
  min-width: 190px;
  white-space: nowrap;
}

.weight-rule-table :deep(th:nth-child(2)),
.weight-rule-table :deep(th:nth-child(3)),
.weight-rule-table :deep(td:nth-child(2)),
.weight-rule-table :deep(td:nth-child(3)) {
  width: 180px;
}

.weight-rule-table :deep(th:nth-child(4)),
.weight-rule-table :deep(td:nth-child(4)) {
  width: 170px;
}

.weight-rule-table :deep(th:nth-child(5)),
.weight-rule-table :deep(td:nth-child(5)) {
  width: 72px;
}

.weight-rule-table :deep(td) {
  vertical-align: middle;
  color: rgba(226, 232, 240, 0.9);
  background: rgba(15, 23, 42, 0.58);
  border-bottom-color: rgba(148, 163, 184, 0.16) !important;
}

.weight-rule-table :deep(td:nth-child(2) .v-input),
.weight-rule-table :deep(td:nth-child(3) .v-input),
.weight-rule-table :deep(td:nth-child(4) .v-input) {
  max-width: 150px;
}

.area-surcharge-table :deep(th:nth-child(1)),
.area-surcharge-table :deep(td:nth-child(1)) {
  width: 240px;
}

.area-surcharge-table :deep(th:nth-child(2)),
.area-surcharge-table :deep(th:nth-child(3)),
.area-surcharge-table :deep(td:nth-child(2)),
.area-surcharge-table :deep(td:nth-child(3)) {
  width: 220px;
}

.weight-rule-table :deep(tbody tr:hover td) {
  background: rgba(205, 178, 122, 0.08);
}

.weight-rule-table :deep(.v-field) {
  background: rgba(8, 13, 24, 0.86);
  color: rgba(226, 232, 240, 0.96);
}

.weight-rule-table :deep(.v-field__input),
.weight-rule-table :deep(.v-field__append-inner),
.weight-rule-table :deep(.v-field__suffix) {
  color: rgba(226, 232, 240, 0.92);
}

.weight-rule-table :deep(.text-medium-emphasis) {
  color: rgba(203, 213, 225, 0.72) !important;
}

@media (max-width: 959px) {
  .shipping-filter {
    grid-template-columns: 1fr;
  }

  .shipping-filter__actions {
    justify-content: flex-start;
  }

  .shipping-section__head {
    flex-direction: column;
  }
}
</style>
